import { extractApiError } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

export interface LoginRequest {
  companyNo: string;
  operatorId: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  companyNo: string;
  customerNo: string;
  operatorId: string;
  operatorName: string;
  operatorGroupId: string;
  tenantName: string;
  defaultWarehouse?: string;
  expiresAt: string;
}

export interface UserContext {
  companyNo: string;
  customerNo: string;
  operatorId: string;
  operatorName: string;
  operatorGroupId: string;
  tenantName?: string;
  defaultWarehouse?: string;
}

export interface ForgotPasswordRequest {
  customerNo: string;
  userId: string;
}

export interface ResetPasswordRequest {
  token: string;
  newPassword: string;
  customerNo: string;
}

export interface ValidateTokenRequest {
  token: string;
  customerNo: string;
}

export class AuthService {
  static async login(request: LoginRequest): Promise<LoginResponse> {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Login failed'));
    }

    return await response.json();
  }

  static async forgotPassword(request: ForgotPasswordRequest): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(extractApiError(data, response, 'Forespørsel feilet'));
    }

    return data;
  }

  static async validateResetToken(request: ValidateTokenRequest): Promise<{ valid: boolean }> {
    const response = await fetch(`${API_BASE_URL}/auth/validate-reset-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      return { valid: false };
    }

    return await response.json();
  }

  static async resetPassword(request: ResetPasswordRequest): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(extractApiError(data, response, 'Tilbakestilling feilet'));
    }

    return data;
  }

  static async logout(): Promise<void> {
    const token = this.getToken();
    if (token) {
      try {
        await fetch(`${API_BASE_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
      } catch (error) {
        console.warn('Logout request failed:', error);
      }
    }
    this.removeToken();
  }

  static setToken(token: string): void {
    localStorage.setItem('bizclick_token', token);
  }

  static getToken(): string | null {
    return localStorage.getItem('bizclick_token');
  }

  static removeToken(): void {
    localStorage.removeItem('bizclick_token');
  }

  static isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 > Date.now();
    } catch {
      return false;
    }
  }

  static getUserDataFromToken(): UserContext | null {
    const token = this.getToken();
    if (!token) return null;

    try {
        //const payload = JSON.parse(atob(token.split('.')[1]));

        const payload = JSON.parse(
            decodeURIComponent(
                atob(token.split('.')[1])
                    .split('')
                    .map(char => `%${('00' + char.charCodeAt(0).toString(16)).slice(-2)}`)
                    .join('')
            )
        );


      return {
        companyNo: payload.CompanyNo,
        customerNo: payload.CustomerNo,
        operatorId: payload.OperatorId,
        operatorName: payload.OperatorName,
        operatorGroupId: payload.OperatorGroupId
      };




    } catch (error) {
      console.error('Failed to parse token:', error);
      return null;
    }
  }

  static async authenticatedFetch(url: string, options: RequestInit = {}): Promise<Response> {
    const token = this.getToken();

    if (!token) {
      throw new Error('No authentication token available');
    }

    // When sending FormData, don't set Content-Type - let the browser set
    // multipart/form-data with the correct boundary automatically.
    const isFormData = options.body instanceof FormData;

    const headers: Record<string, string> = {
      'Authorization': `Bearer ${token}`,
      ...(options.headers as Record<string, string>)
    };

    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(url, {
      ...options,
      headers
    });

    if (response.status === 401) {
      this.removeToken();
      window.location.reload();
    }

    return response;
  }

  static async authenticatedGet(url: string, options?: RequestInit): Promise<Response> {
    return this.authenticatedFetch(url, { method: 'GET', ...options });
  }

  static async authenticatedPost(url: string, data?: any): Promise<Response> {
    return this.authenticatedFetch(url, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  static async authenticatedPut(url: string, data?: any): Promise<Response> {
    return this.authenticatedFetch(url, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  static async authenticatedPatch(url: string, data?: any): Promise<Response> {
    return this.authenticatedFetch(url, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  static async authenticatedDelete(url: string, data?: any): Promise<Response> {
    return this.authenticatedFetch(url, {
      method: 'DELETE',
      body: data ? JSON.stringify(data) : undefined
    });
  }
}
